import { useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http, formatApiError } from "@/lib/api";
import { CustomActionStep } from "@/components/portal/CustomActionStep";
import { ACTION_ICONS } from "@/lib/actionIcons";
import { TextFormatBar } from "@/components/admin/TextFormatBar";

export const ACTION_TYPES = {
  MENSAGEM: { label: "Mensagem ao cliente", hint: "O cliente apenas lê o aviso, sem botão. O operador avança para a próxima etapa pelo painel." },
  CAMPO: { label: "Pedir informação", hint: "O cliente preenche um campo e a resposta aparece para o operador." },
  CODIGO: { label: "Pedir código", hint: "O operador informa o código enviado; o cliente digita e o sistema confere." },
};

const EMPTY = { name: "", type: "MENSAGEM", title: "", description: "", field_label: "", field_kind: "texto", field_length: 4, digits: 6, button_label: "", text_align: "center", icon: "sparkles", icon_url: "", active: true };
const cls = "border-white/10 bg-black/30 text-slate-100";
const L = ({ children }) => <label className="mb-1 mt-3 block text-xs text-slate-400">{children}</label>;

export const ActionFormDialog = ({ action, onClose, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...action });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const descRef = useRef(null);

  const save = async () => {
    setBusy(true);
    const { id, created_at, updated_at, created_by, ...body } = f;
    try {
      if (id) await http.put(`/admin/actions/${id}`, { ...body, digits: Number(body.digits), field_length: Number(body.field_length) || 0 });
      else await http.post("/admin/actions", { ...body, digits: Number(body.digits), field_length: Number(body.field_length) || 0 });
      toast.success("Funcionalidade salva.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#141C2E] text-slate-100 sm:max-w-2xl" data-testid="feature-dialog">
        <DialogHeader>
          <DialogTitle>{f.id ? "Editar funcionalidade" : "Nova funcionalidade"}</DialogTitle>
          <DialogDescription className="text-slate-400">A funcionalidade ativa aparece como botão no quadro Ações.</DialogDescription>
        </DialogHeader>
        <L>Nome do botão no painel</L>
        <Input value={f.name} maxLength={60} onChange={(e) => set("name", e.target.value)} placeholder="Ex.: Confirmar data de nascimento" className={cls} data-testid="feature-name" />
        <L>Tipo</L>
        <Select value={f.type} onValueChange={(v) => set("type", v)}>
          <SelectTrigger className={cls} data-testid="feature-type"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#141C2E] text-slate-100">
            {Object.entries(ACTION_TYPES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`feature-type-${k}`}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="mt-1 text-xs text-slate-500">{ACTION_TYPES[f.type].hint}</p>
        <L>Título exibido ao cliente</L>
        <Input value={f.title} maxLength={120} onChange={(e) => set("title", e.target.value)} className={cls} data-testid="feature-title" />
        <L>Texto / instruções para o cliente</L>
        {f.type === "MENSAGEM" && <TextFormatBar textareaRef={descRef} value={f.description} onChange={(v) => set("description", v)} align={f.text_align} onAlign={(v) => set("text_align", v)} testPrefix="feature-fmt" />}
        <Textarea ref={descRef} value={f.description} maxLength={1000} onChange={(e) => set("description", e.target.value)} className={`${cls} min-h-[90px]`} data-testid="feature-description" />
        {f.type === "CAMPO" && (
          <div className="grid grid-cols-2 gap-3">
            <div><L>Rótulo do campo</L><Input value={f.field_label} maxLength={80} onChange={(e) => set("field_label", e.target.value)} className={cls} data-testid="feature-field-label" /></div>
            <div><L>Formato</L>
              <Select value={f.field_kind} onValueChange={(v) => set("field_kind", v)}>
                <SelectTrigger className={cls} data-testid="feature-field-kind"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#141C2E] text-slate-100"><SelectItem value="texto">Texto</SelectItem><SelectItem value="numero">Somente números</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        )}
        {f.type === "CAMPO" && f.field_kind === "numero" && (
          <>
            <L>Quantidade de números que o cliente deve digitar (1 a 20)</L>
            <Input type="number" min={1} max={20} value={f.field_length || ""} onChange={(e) => set("field_length", Math.max(0, Math.min(20, Number(e.target.value) || 0)))} className={`${cls} w-28 font-mono-x`} data-testid="feature-field-length" />
            <p className="mt-1 text-xs text-slate-500">O cliente verá {f.field_length || "N"} quadradinho(s) para preencher.</p>
          </>
        )}
        {f.type === "CODIGO" && (<><L>Quantidade de dígitos (4 a 8)</L><Input type="number" min={4} max={8} value={f.digits} onChange={(e) => set("digits", e.target.value)} className={`${cls} w-28`} data-testid="feature-digits" /></>)}
        <L>Ícone exibido ao cliente</L>
        <div className="flex flex-wrap gap-2" data-testid="feature-icon-picker">
          {Object.entries(ACTION_ICONS).map(([k, Icon]) => (
            <button key={k} type="button" onClick={() => setF((p) => ({ ...p, icon: k, icon_url: "" }))} data-testid={`feature-icon-${k}`}
              className={`flex h-10 w-10 items-center justify-center rounded-md border transition-colors ${f.icon === k && !f.icon_url ? "border-[var(--brand)] bg-white/5" : "border-white/10 hover:border-white/30"}`}>
              <Icon className="h-5 w-5" style={{ color: "var(--brand)" }} />
            </button>
          ))}
        </div>
        <L>Ou use uma imagem/logo própria (URL HTTPS, opcional)</L>
        <Input value={f.icon_url} maxLength={1000} onChange={(e) => set("icon_url", e.target.value.trim())} placeholder="https://..." className={cls} data-testid="feature-icon-url" />
        {f.type !== "MENSAGEM" && <L>Texto do botão do cliente (opcional)</L>}
        {f.type !== "MENSAGEM" && <Input value={f.button_label} maxLength={40} onChange={(e) => set("button_label", e.target.value)} placeholder="Confirmar" className={cls} data-testid="feature-button-label" />}
        <div className="mt-4" data-testid="feature-preview">
          <p className="mb-2 text-xs uppercase tracking-[0.16em] text-slate-400">Pré-visualização para o cliente</p>
          <div className="portal-bg pointer-events-none flex justify-center rounded-md p-4">
            <CustomActionStep key={`${f.type}-${f.field_kind}-${f.field_length}-${f.digits}`} state={{ custom_action: { ...f, title: f.title || "Título exibido ao cliente", digits: Number(f.digits) || 6, field_length: Number(f.field_length) || 0, instance_id: "preview", status: "AGUARDANDO", attempts: 0, max_attempts: 5 } }} />
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">Nunca use funcionalidades para pedir senhas bancárias, tokens de banco ou dados de cartão.</p>
        <DialogFooter className="mt-2">
          <button onClick={onClose} className="btn-ghost-sm" data-testid="feature-cancel">Cancelar</button>
          <button onClick={save} disabled={busy || !f.name.trim() || !f.title.trim() || (f.type === "CAMPO" && f.field_kind === "numero" && !(f.field_length >= 1))} className="btn-gold h-9 px-4 text-sm" data-testid="feature-save">Salvar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
